// Shared cache + helper for lazy mix-link resolution.
//
// Mirror of lazyResolveCache.js for mixes. Key differences from the track
// flow:
//   - No sample fallback (mixes have no Apple sample equivalent). On miss,
//     callers show a toast + hide the play button for the session.
//   - Cross-platform recovery: a dead Mixcloud / SoundCloud / hearthis link
//     can be replaced by a YouTube link — we always search YouTube
//     regardless of the original platform.
//   - Cache value is the full mix URL (not a video id) because mixes can
//     live on any of YT/SC/Mixcloud/hearthis.
//
// Convention: cache key is `String(mixId)` where mixId is the id
// the server exposes as `id`.

import { entityId } from './entityId';

const _cache = new Map();              // mixId → mixLink (string)
const _inflight = new Map();           // mixId → Promise<resolve result>

function _currentDjpalToken() {
    try { return localStorage.getItem('djpal:token') || null; } catch { return null; }
}

export function getCachedMixLink(mixId) {
    if (!mixId) return null;
    return _cache.get(String(mixId)) || null;
}

export function setCachedMixLink(mixId, link) {
    if (!mixId || !link) return;
    const key = String(mixId);
    _cache.set(key, link);
    try {
        window.dispatchEvent(new CustomEvent('djpal-mix-link-resolved', {
            detail: { mixId: key, link },
        }));
    } catch { /* no window — ignore */ }
}

// Fire (or share) the lazy-resolve IPC for mixes. Returns:
//   - { link: '...', cached: true } when cache had it
//   - the raw IPC response object when freshly resolved (may have link)
//   - null when not applicable / errored / timed out
//
// opts.replaceLink — set to the currently-dead mix link to trigger the
// REPLACEMENT path. When unset, behaves as a first-fill resolve (gated by
// LAZY_MIX_RESOLVE_MODE; default DISABLED).
// opts.extractError — when replacing, pass the extraction error string so
// the main process can short-circuit on rate-limit / bot challenges.
export async function resolveMixLinkLazy(mix, opts = {}) {
    if (!mix) return null;
    if (typeof window === 'undefined' || !window.electron?.djpal?.resolveMixLink) return null;

    const mixId = String(entityId(mix) || '').trim();
    if (!mixId) return null;

    const cached = _cache.get(mixId);
    if (cached) return { link: cached, cached: true };

    const existing = _inflight.get(mixId);
    if (existing) return opts.timeoutMs ? _withTimeout(existing, opts.timeoutMs) : existing;

    const promise = window.electron.djpal.resolveMixLink({
        mixId,
        artist: mix.artistsname || mix.artist || '',
        eventName: mix.event_name || mix.eventName || '',
        date: mix.date || '',
        replaceLink: opts.replaceLink || null,
        extractError: opts.extractError || null,
        token: opts.token ?? _currentDjpalToken(),
    }).then((result) => {
        const link = result?.link || null;
        if (link) setCachedMixLink(mixId, link);
        return result || null;
    }).catch(() => null);

    _inflight.set(mixId, promise);
    promise.finally(() => _inflight.delete(mixId));

    return opts.timeoutMs ? _withTimeout(promise, opts.timeoutMs) : promise;
}

function _withTimeout(promise, ms) {
    let to;
    const timer = new Promise((resolve) => { to = setTimeout(() => resolve(null), ms); });
    return Promise.race([promise.finally(() => clearTimeout(to)), timer]);
}
